import WButton from "../wbutton/WButton";
import { useRouter } from "next/router";
import WLink from "../wlink/WLink";
import WIconButton from "../wicon-button/WIconButton";
import { useEffect, useState } from "react";
import clsx from "clsx";
import LHelloBar from "../lhello-bar/LHelloBar";
import Sidebar from "./Sidebar";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import BottomTabsContainer from "../layout/BottomTabsContainer";

// Define the Link interface (ensure it matches the one in Sidebar.tsx)
interface Link {
  text: string;
  href?: string;
  icon?: string;
  onClick?: () => void;
  subitems?: Array<Link>;
}

/* eslint-disable-next-line */
export interface LHeaderProps {
  image: {
    url: string;
    altTag: string;
  };
  button?: {
    text: string;
    url: string;
  };
  helloBarContent?: string; // Richtext
  links?: Array<Link>; // Make links configurable
}

// Define default navigation links
import defaultLinks from "./links.json";

export const LHeader = (props: LHeaderProps) => {
  const router = useRouter();
  const links: Link[] = props.links || defaultLinks;

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const [hoverTimer, setHoverTimer] = useState<NodeJS.Timeout | null>(null);
  const [closeTimer, setCloseTimer] = useState<NodeJS.Timeout | null>(null);

  const HOVER_DELAY = 100; // Delay in milliseconds
  const CLOSE_DELAY = 300; // Longer delay for closing to prevent accidental closures

  const hideNav = () => setShowMobileMenu(false);
  useEffect(() => {
    router?.events?.on("routeChangeStart", hideNav);
  }, [router?.events]);

  const scrolltoHash = (elementId: string) => {
    const element = document.getElementById(elementId);
    element?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  // Handle menu opening with delay
  const handleMenuOpen = (index: number) => {
    // Clear any existing close timer
    if (closeTimer) {
      clearTimeout(closeTimer);
      setCloseTimer(null);
    }

    // Set a timer to open the menu
    const timer = setTimeout(() => {
      setHoveredItem(index);
    }, HOVER_DELAY);

    setHoverTimer(timer);
  };

  // Handle menu closing with delay
  const handleMenuClose = (e: React.MouseEvent, index: number) => {
    // Clear any existing hover timer
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }

    // Check if moving to dropdown
    const relatedTarget = e.relatedTarget;
    const isMovingToDropdown =
      relatedTarget && relatedTarget instanceof HTMLElement
        ? relatedTarget.closest(`.dropdown-${index}`) !== null
        : false;

    if (!isMovingToDropdown) {
      // Set a timer to close the menu
      const timer = setTimeout(() => {
        setHoveredItem(null);
      }, CLOSE_DELAY);

      setCloseTimer(timer);
    }
  };

  // Cancel any pending timers when component unmounts
  useEffect(() => {
    return () => {
      if (hoverTimer) clearTimeout(hoverTimer);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [hoverTimer, closeTimer]);

  // Check if a link is active based on the current URL
  const isLinkActive = (href?: string) => {
    if (!href) return false;
    return router.asPath === href;
  };

  // Check if a link or any of its subitems is active
  const hasActiveChild = (link: Link): boolean => {
    if (isLinkActive(link.href)) return true;
    if (!link.subitems) return false;
    return link.subitems.some(
      (subitem) => isLinkActive(subitem.href) || hasActiveChild(subitem)
    );
  };

  return (
    <div className={clsx(["fixed top-0 z-[150] w-full header-component"])}>
      {props?.helloBarContent && <LHelloBar content={props?.helloBarContent} />}
      <div className="bg-ekblue backdrop-blur-sm">
        <div className="container px-4 py-2 mx-auto lg:py-3 lg:max-w-7xl">
          <div className="z-[500] flex items-center justify-between">
            {/* Logo */}
            <div className="flex justify-between w-full lg:w-auto">
              <a
                href="/"
                className="relative z-20 flex-shrink-0 w-40 h-10 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  router?.push("/");
                }}
              >
                <img
                  src={props?.image?.url}
                  alt={props?.image?.altTag}
                  className="relative w-full h-full object-contain"
                />
              </a>
            </div>

            {/* Mobile Sidebar */}
            <Sidebar image={props.image} button={props.button} links={links} />

            {/* Desktop Navigation */}
            <nav
              aria-label="Main navigation"
              className={clsx(
                "fixed top-0 z-50 flex-col left-0 lg:flex items-center w-full h-screen gap-5 px-4 py-2 lg:gap-10 lg:p-0 bg-ekblue lg:h-auto lg:flex-row lg:bg-transparent lg:relative lg:w-auto",
                showMobileMenu ? "flex" : "hidden"
              )}
            >
              {/* Mobile Header - Only visible on mobile */}
              <div className="flex justify-between w-full mb-10 lg:hidden">
                <img
                  src={props?.image?.url}
                  alt={props?.image?.altTag}
                  className="relative z-20 flex-shrink-0 h-10 w-44"
                  onClick={() => {
                    router?.push("/");
                    hideNav();
                  }}
                />
                <WIconButton
                  icon={faXmark}
                  type="transparent-white"
                  className="text-2xl"
                  onClick={() => setShowMobileMenu(false)}
                />
              </div>
              {/* Main Navigation List */}
              <ul className="flex flex-col lg:flex-row items-center gap-5 w-full lg:w-auto">
                {links.map((link, index) => (
                  <li
                    key={index}
                    className="relative group w-full lg:w-auto"
                    onMouseEnter={() => handleMenuOpen(index)}
                    onMouseLeave={(e) => handleMenuClose(e, index)}
                  >
                    <WLink
                      simpleLink
                      href={link.href}
                      text={link.text}
                      onClick={() => {
                        if (link.onClick) {
                          link.onClick();
                        } else if (link.href) {
                          router?.push(link.href);
                        }
                        hideNav();
                      }}
                      className={clsx(
                        isLinkActive(link.href) || hasActiveChild(link)
                          ? "!text-ekgreen"
                          : "text-eklight",
                        "flex items-center"
                      )}
                      aria-expanded={
                        link.subitems && hoveredItem === index
                          ? "true"
                          : "false"
                      }
                      aria-haspopup={link.subitems ? "true" : "false"}
                    />

                    {/* Dropdown Menu */}
                    {link.subitems && link.subitems.length > 0 && (
                      <ul
                        className={clsx(
                          `absolute z-50 mt-2 bg-ekblue rounded shadow-lg py-2 min-w-[180px] dropdown-${index}`,
                          hoveredItem !== index && "hidden",
                          index <= link.subitems.length / 2
                            ? "-left-2"
                            : "-right-2"
                        )}
                        role="menu"
                        aria-label={`${link.text} submenu`}
                        onMouseEnter={() => {
                          if (closeTimer) {
                            clearTimeout(closeTimer);
                            setCloseTimer(null);
                          }
                          setHoveredItem(index);
                        }}
                        onMouseLeave={() => {
                          const timer = setTimeout(() => {
                            setHoveredItem(null);
                          }, CLOSE_DELAY);
                          setCloseTimer(timer);
                        }}
                      >
                        {link.subitems.map((subitem, subIndex) => (
                          <li key={subIndex} role="none">
                            <WLink
                              simpleLink
                              href={subitem.href}
                              text={subitem.text}
                              onClick={() => {
                                if (subitem.onClick) {
                                  subitem.onClick();
                                } else if (subitem.href) {
                                  router?.push(subitem.href);
                                }
                                hideNav();
                              }}
                              className={clsx(
                                "block px-4 py-2 hover:bg-ekblue-dark whitespace-nowrap",
                                isLinkActive(subitem.href)
                                  ? "!text-ekgreen"
                                  : "text-eklight"
                              )}
                              role="menuitem"
                            />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
              {/* CTA Button */}
              {props?.button?.url && (
                <div className="w-full lg:w-auto">
                  <WButton
                    type="primary-small"
                    key={props?.button?.url}
                    text={props?.button?.text}
                    onClick={() => {
                      props?.button?.url && router?.push(props?.button?.url);
                      hideNav();
                    }}
                    className="z-20"
                  />
                </div>
              )}
            </nav>
          </div>
        </div>
        <BottomTabsContainer />
      </div>
    </div>
  );
};

export default LHeader;
